import { Collapse } from "@blueprintjs/core";
import classnames from "classnames";
import { observer } from "mobx-react";
import { Caret } from "modules/common/components/collapse/Caret";
import { TextLink } from "modules/common/components/routing/TextLink";
import { SectionCollapser } from "modules/common/models/section-collapser";
import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { columnLeft, columnsWidth, PlanGrid } from "modules/spending/workload-plan/models/plan-scale";
import { columnAt, SECTION_PADDING } from "modules/spending/workload-plan/components/plan-geometry";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlanLinks } from "../models/plan-links";
import { PlanOperationInput } from "../models/plan-draft";
import { AbsenceBand, conflictRanges, ConflictRange, PlanConflict, PlanConflictCells } from "../models/plan-conflicts";
import { ProjectPlanDetail, ProjectPlanProject } from "../models/project-plan-types";
import { BarMode, DraggableBar } from "./DraggableBar";
import { ShiftFromTarget } from "./ShiftFromDialog";
import { ConflictZones, PlanTrack, trackStyles } from "./PlanTrack";
import {
    HiddenMilestones,
    MilestoneAnchor,
    MilestoneHandlers,
    MilestonesLayer,
    TrackViewport,
} from "./MilestonesLayer";
import { ProjectPlanSectionRow, ProjectPlanStageCaption } from "./ProjectPlanSectionRow";
import { planRowKey, ProjectPlanRowView } from "./ProjectPlanRowView";
import { BusyBand, foreignBusy } from "../models/plan-busy";
import styles from "./ProjectSection.module.scss";

/**
 * Ширина колонки проектов. Шире табельных 240px: здесь две строки — название
 * с номером и «до … · ГИП …». Обязана совпадать с --wp-name-width в
 * ProjectPlanPage.module.scss, иначе шапка сетки разъедется со строками.
 */
const PROJECT_NAME_WIDTH = 300;

/** Сколько ждать второго клика, прежде чем свернуть проект по клику на полосу, мс */
const DOUBLE_CLICK_DELAY = 250;

/** Где начинается шкала строки: отступ секции + колонка названий. Координаты — без зума */
export const PROJECT_TRACK_OFFSET = SECTION_PADDING + PROJECT_NAME_WIDTH;

interface ProjectSectionProps {
    project: ProjectPlanProject;
    grid: PlanGrid;
    monthBreaks: number[];
    selectedSlot: { start: number; length: number } | null;
    collapsed: boolean;
    onToggle: (projectId: string) => void;
    /** Во что раскрывается проект: сотрудники или разделы состава */
    detail: ProjectPlanDetail;
    links: PlanLinks;
    /** Сворачиватель страницы — им же раскрываются разделы (ключ «проект/раздел») */
    collapser: typeof SectionCollapser.Type;
    /** Сколько колонок сетки видно — вехи правее считаются скрытыми */
    columnsCount: number;
    /** Вехи, выделенные сейчас на странице */
    activeMilestoneKey: string | null;
    milestoneHandlers: MilestoneHandlers;
    /** Новая веха проекта: на колонку двойного клика или без даты (кнопка «+»); окно — у anchor */
    onAddMilestone: (project: ProjectPlanProject, column: number | null, anchor: MilestoneAnchor) => void;
    /** Видимая часть шкалы — по ней считаются вехи, ушедшие за край экрана */
    viewport: TrackViewport | null;
    /** Прокрутить график к точке шкалы */
    onReveal: (center: number) => void;
    /** Режим правки графика включён */
    editMode?: boolean;
    /** Сотрудники, чьи работы пользователь двигает как начальник отдела */
    editableEmployers?: Set<string>;
    /** Даты колонок сетки — край срока встаёт на дату колонки */
    days?: string[];
    /** Операция черновика; пока идёт, полоса стоит на новом месте */
    onOperation?: (operation: PlanOperationInput) => Promise<unknown> | void;
    /** Правый клик по полосе проекта или раздела в режиме правки — «Сдвинуть с даты» */
    onShiftFrom?: (target: ShiftFromTarget) => void;
    /** Конфликты, в которых участвует проект */
    conflicts?: PlanConflict[];
    /** Колонка сетки по дате — для подсветки конфликтов */
    columns?: Map<string, number>;
    /** Строки, к которым перешли из панели конфликтов (planRowKey) */
    highlightedRows?: Set<string>;
    /** Плановые отсутствия сотрудников полосами — по сотруднику */
    absences?: Map<string, AbsenceBand[]>;
    /** Вся занятость сотрудников по проектам и разделам; не задана — не показывается */
    busy?: Map<string, BusyBand[]>;
    /** Клик по занятости — к строке сотрудника в том проекте */
    onBusyClick?: (band: BusyBand, employerId: string) => void;
    /** Окно связей работы (правый клик по её полосе) */
    onOpenWorkLinks?: (orderId: string, assignmentId: string, employerId: string) => void;
    /** Окно связей раздела */
    onOpenSectionLinks?: (orderId: string, contentGuid: string, title: string) => void;
    /** Можно протягивать связи — полосы помечаются, точки у концов рисует LinksLayer */
    linkConnectable?: boolean;
}

const NO_CONFLICTS: PlanConflict[] = [];

/** Ключ раскрытия раздела: у «Без раздела» guid пустой */
const sectionKey = (projectId: string, sectionId: string) => `${projectId}/${sectionId || "none"}`;

/**
 * Проект в списке — строка обычной таблицы, которая раскрывается в графики участников.
 * В свёрнутом виде показывает сводную полосу и крайний день работ: ради этого экран и нужен.
 */
export const ProjectSection: React.FC<ProjectSectionProps> = observer((props) => {
    const {
        project,
        grid,
        monthBreaks,
        selectedSlot,
        collapsed,
        onToggle,
        detail,
        links,
        collapser,
        columnsCount,
        activeMilestoneKey,
        milestoneHandlers,
        onAddMilestone,
        viewport,
        onReveal,
        editMode,
        editableEmployers,
        days,
        onOperation,
        onShiftFrom,
        conflicts = NO_CONFLICTS,
        columns,
        highlightedRows,
        absences,
        busy,
        onBusyClick,
        onOpenWorkLinks,
        onOpenSectionLinks,
        linkConnectable,
    } = props;

    // занятость строки — чужие проекты (и разделы); тем же массивом между рендерами, ради React.memo
    const rowBusy = useMemo(() => {
        const cache = new Map<string, BusyBand[] | undefined>();

        return (employerId: string, section?: string) => {
            const key = `${employerId}/${section === undefined ? "*" : section}`;

            if (!cache.has(key)) {
                const bands = busy ? foreignBusy(busy.get(employerId), project.id, section) : [];
                cache.set(key, bands.length ? bands : undefined);
            }

            return cache.get(key);
        };
    }, [busy, project.id]);

    const isHighlighted = (employerId: string, sectionId?: string) =>
        !!highlightedRows && highlightedRows.has(planRowKey(project.id, employerId, sectionId));

    /** Подсветка конфликтов: строка проекта, строка раздела, строка исполнителя */
    const ranges = useCallback(
        (match: (cells: PlanConflictCells) => boolean, withEmployer = false): ConflictRange[] | undefined =>
            conflicts.length && columns
                ? conflictRanges(
                      conflicts,
                      columns,
                      (cells) => cells.orderId === project.id && match(cells),
                      project.id,
                      withEmployer
                  )
                : undefined,
        [conflicts, columns, project.id]
    );

    // строка проекта сводная — в подсказке конфликта нужна фамилия
    const projectConflicts = useMemo(() => ranges(() => true, true), [ranges]);

    // строки — React.memo: одинаковая подсветка должна приходить тем же массивом
    const rowRanges = useMemo(() => {
        const cache = new Map<string, ConflictRange[] | undefined>();

        return (key: string, match: (cells: PlanConflictCells) => boolean, withEmployer = false) => {
            if (!cache.has(key)) {
                cache.set(key, ranges(match, withEmployer));
            }

            return cache.get(key);
        };
    }, [ranges]);

    const canEditMilestones = project.canEditMilestones;

    /** Подложка под линии вех — состоянием, а не ref: вехи рисуют в неё, как только она появится */
    const [milestoneLines, setMilestoneLines] = useState<HTMLDivElement | null>(null);

    // проект, разделы и сроки — все проекты или ГИП (как у вех); работы — ещё и начальник
    // отдела для своих сотрудников
    const canEditProject = !!editMode && !!onOperation && canEditMilestones;

    // полоса проекта тянется, только если есть что сдвигать — собственные будущие работы;
    // у проекта с одним подрядчиком её перенос увёл бы в одиночку вехи и сроки
    const hasMovableWorks = project.rows.some(
        (row) =>
            row.kind === "employer" &&
            row.segments.some((segment) => !!segment.assignmentId && !segment.isPast && !segment.ghost)
    );
    const canEditRow = useCallback(
        (employerId: string) =>
            !!editMode &&
            !!onOperation &&
            (canEditMilestones || (!!editableEmployers && editableEmployers.has(employerId))),
        [editMode, onOperation, canEditMilestones, editableEmployers]
    );

    const commitProject = useCallback(
        (mode: BarMode, delta: number) => {
            if (!onOperation || !project.summary) {
                return;
            }

            if (mode === "move") {
                return onOperation({ kind: "project", orderId: project.id, shift: delta });
            }

            // правый край полосы проекта — его плановый срок; договор не трогается
            if (mode === "end" && days && days.length) {
                const column = project.summary.start + project.summary.length - 1 + delta;
                return onOperation({
                    kind: "project-end",
                    orderId: project.id,
                    day: days[Math.max(0, Math.min(column, days.length - 1))],
                });
            }
        },
        [onOperation, project.summary, project.id, days]
    );

    /** Правый клик по строке проекта — «Сдвинуть с даты» от дня под курсором */
    const shiftProjectFrom = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!canEditProject || !onShiftFrom || !days) {
                return;
            }

            event.preventDefault();
            const column = columnAt(event.currentTarget, event.clientX, grid);

            onShiftFrom({
                kind: "project",
                orderId: project.id,
                title: `${project.fullInventoryNumber} ${project.name}`,
                day: days[Math.max(0, column)] || days[0],
            });
        },
        [canEditProject, onShiftFrom, days, grid, project.id, project.fullInventoryNumber, project.name]
    );

    /** Веха в черновик: сдвиг в рабочих днях, как у полос */
    const moveMilestone = useCallback(
        (milestoneId: string, shift: number) =>
            onOperation ? onOperation({ kind: "milestone", milestoneId, shift }) : undefined,
        [onOperation]
    );

    /**
     * Плановый срок позже конца работ — полоса продолжается до него бледным «хвостом»:
     * работ там нет, а срок есть. Правый край хвоста — сам срок, его и тянут.
     */
    const tail = useMemo(() => {
        const summaryEnd = project.summary ? project.summary.start + project.summary.length - 1 : -1;

        return project.summary && project.planEndColumn !== null && project.planEndColumn > summaryEnd
            ? {
                  start: summaryEnd + 1,
                  // срок за правым краем сетки — хвост до края, с «зубцами»
                  length: Math.min(project.planEndColumn, columnsCount - 1) - summaryEnd,
                  continuesRight: project.planEndColumn >= columnsCount,
                  isPast: !!project.planEnd && project.planEnd < moment().format("YYYY-MM-DD"),
              }
            : null;
    }, [project.summary, project.planEndColumn, project.planEnd, columnsCount]);

    /** Правый край хвоста: новая дата планового срока */
    const commitTail = useCallback(
        (mode: BarMode, delta: number) => {
            if (!onOperation || !tail || mode !== "end" || !days || !days.length) {
                return;
            }

            const column = tail.start + tail.length - 1 + delta;

            return onOperation({
                kind: "project-end",
                orderId: project.id,
                day: days[Math.max(0, Math.min(column, days.length - 1))],
            });
        },
        [onOperation, tail, days, project.id]
    );

    // засечка срока — только когда работы вышли за него: иначе срок и так виден концом хвоста
    const planEnd =
        !tail && project.planEndColumn !== null && project.planEndColumn >= 0 && project.planEndColumn < columnsCount
            ? columnLeft(grid, project.planEndColumn) + columnsWidth(grid, project.planEndColumn, 1)
            : null;

    /**
     * Двойной клик по дню полосы — новая веха, и проект при этом раскрываться или
     * сворачиваться не должен. Поэтому клик по полосе проекта, у которого можно ставить
     * вехи, сворачивает строку с задержкой: пришёл второй клик — сворачивание отменяется.
     * Клик по колонке названия сворачивает сразу.
     */
    const toggleTimer = useRef<number>(undefined);

    useEffect(() => () => window.clearTimeout(toggleTimer.current), []);

    const toggle = useCallback(
        (event: React.MouseEvent) => {
            window.clearTimeout(toggleTimer.current);

            if (event.detail > 1) {
                return;
            }

            const onTrack = !!(event.target as Element).closest(`.${styles.track}`);

            if (onTrack && canEditMilestones) {
                toggleTimer.current = window.setTimeout(() => onToggle(project.id), DOUBLE_CLICK_DELAY);
                return;
            }

            onToggle(project.id);
        },
        [onToggle, project.id, canEditMilestones]
    );

    const addMilestone = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();

            const rect = event.currentTarget.getBoundingClientRect();
            onAddMilestone(project, null, { x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
        },
        [onAddMilestone, project]
    );

    /** Двойной клик по дню строки проекта — новая веха на этот день */
    const addMilestoneAt = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!canEditMilestones) {
                return;
            }

            // первый клик двойного уже поставил сворачивание в очередь — отменяем
            window.clearTimeout(toggleTimer.current);

            const column = columnAt(event.currentTarget, event.clientX, grid);

            if (column >= 0) {
                const row = event.currentTarget.getBoundingClientRect();
                onAddMilestone(project, column, { x: event.clientX, top: row.top, bottom: row.bottom });
            }
        },
        [canEditMilestones, grid, onAddMilestone, project]
    );

    // Свёрнутый проект не рендерит строк, и max-content схлопнул бы блок до ширины
    // названия. Держим ширину сетки явно — строка проекта идёт во всю таблицу.
    // Отступ считается один раз: он только слева, у .header и .rows.
    const width = PROJECT_NAME_WIDTH + grid.totalWidth + SECTION_PADDING;

    const lastDay = project.lastDay ? moment(project.lastDay, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY") : "";

    // Вторая строка колонки: срок и ГИП. Срок первым — ради него экран и открывают,
    // а имя при нехватке места уйдёт в многоточие, не спрятав дату.
    const hasSubtitle = !!lastDay || !!project.employer;

    return (
        // Цвет проекта уходит вниз одной CSS-переменной: им подсвечиваются левая
        // полоса, нижняя граница заголовка и нижняя граница последней строки —
        // раскрытый проект читается как один блок своего цвета.
        <div
            className={styles.block}
            data-plan-project={project.id}
            style={{ minWidth: width, "--project-color": project.paint.chipBackground } as React.CSSProperties}
        >
            {/* линии вех во всю высоту блока: свёрнут — по строке проекта, раскрыт — по всему составу */}
            {project.milestones.length > 0 && (
                <div className={styles.milestoneLines} style={{ left: PROJECT_TRACK_OFFSET }} ref={setMilestoneLines} />
            )}

            <div
                className={classnames(styles.header, { [styles.expanded]: !collapsed })}
                // строка проекта — куда ведёт стрелка связи, если проект свёрнут
                data-project-row={project.id}
                onClick={toggle}
            >
                {/* подсказка своя, а не нативный title: тот держит паузу около секунды */}
                <div
                    className={styles.name}
                    data-tip={`${project.fullInventoryNumber} ${project.name}`}
                    data-tip-note={[
                        lastDay && `Крайний день работ — ${lastDay}`,
                        project.employer && `ГИП ${project.employer}`,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                >
                    <Caret collapsed={collapsed} />

                    {/*
                        Крайний день стоит в колонке названия, а не у полосы: колонка
                        липкая, поэтому дата видна всегда. У полосы она пряталась за
                        горизонтальной прокруткой ровно на тех проектах, где нужнее всего —
                        на длинных, уезжающих вправо.
                    */}
                    <span
                        className={classnames(styles.inventory, {
                            [styles.inventoryDark]: project.paint.chipDark,
                            [styles.inventoryLight]: !project.paint.chipDark,
                        })}
                        style={{ backgroundColor: project.paint.chipBackground }}
                    >
                        {project.fullInventoryNumber}
                    </span>

                    {canEditMilestones && (
                        <PlanrButton
                            type="greenish"
                            size="small"
                            round={true}
                            icon="general-plus-big"
                            className={styles.addMilestone}
                            title="Добавить веху"
                            onClick={addMilestone}
                        />
                    )}

                    <div className={styles.info}>
                        <div className={styles.title}>
                            <TextLink to={links.order(project.id)}>{project.name}</TextLink>
                        </div>

                        {hasSubtitle && (
                            <div className={styles.lastDay}>
                                {lastDay && `до ${lastDay}`}
                                {lastDay && project.employer && " · "}
                                {project.employer && (
                                    <TextLink to={links.employee(project.employerId)}>{project.employer}</TextLink>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <PlanTrack
                    grid={grid}
                    monthBreaks={monthBreaks}
                    selectedSlot={selectedSlot}
                    className={styles.track}
                    conflicts={projectConflicts}
                    onDoubleClick={addMilestoneAt}
                    onContextMenu={canEditProject && onShiftFrom ? shiftProjectFrom : undefined}
                >
                    {project.summaryParts.map((part) => {
                        const partClass = classnames(trackStyles.block, styles.summary, {
                            [trackStyles.blockDark]: project.paint.dark,
                            [trackStyles.blockLight]: !project.paint.dark,
                            [trackStyles.cutLeft]: part.continuesLeft,
                            [trackStyles.cutRight]: part.continuesRight,
                            // прошлое бледное и не двигается — как у работ и в Табеле План
                            [trackStyles.blockPast]: part.isPast,
                        });

                        const partStyle = {
                            backgroundColor: project.paint.background,
                            borderColor: project.paint.borderColor,
                        };

                        if (canEditProject && hasMovableWorks && !part.isPast) {
                            return (
                                <DraggableBar
                                    key="current"
                                    grid={grid}
                                    start={part.start}
                                    length={part.length}
                                    enabled={true}
                                    // есть хвост срока — край срока у него, а не у работ
                                    edges={{ end: !part.continuesRight && !tail }}
                                    className={partClass}
                                    style={partStyle}
                                    tip={`${project.fullInventoryNumber} ${project.name}`}
                                    tipNote="Тяните — сдвинуть проект целиком; правый край — плановый срок"
                                    linkEnd={`project:${project.id}`}
                                    onCommit={commitProject}
                                >
                                    <ConflictZones
                                        grid={grid}
                                        start={part.start}
                                        length={part.length}
                                        conflicts={projectConflicts}
                                    />
                                </DraggableBar>
                            );
                        }

                        return (
                            <div
                                key={part.isPast ? "past" : "current"}
                                className={partClass}
                                style={{
                                    ...partStyle,
                                    left: columnLeft(grid, part.start),
                                    width: columnsWidth(grid, part.start, part.length),
                                }}
                                data-tip={`${project.fullInventoryNumber} ${project.name}`}
                                data-tip-note={lastDay ? `Крайний день работ — ${lastDay}` : ""}
                                data-project-bar={true}
                            >
                                <ConflictZones
                                    grid={grid}
                                    start={part.start}
                                    length={part.length}
                                    conflicts={projectConflicts}
                                />
                            </div>
                        );
                    })}

                    {tail && tail.length > 0 && (
                        <DraggableBar
                            grid={grid}
                            start={tail.start}
                            length={tail.length}
                            enabled={canEditProject && !tail.isPast}
                            moveDisabled={true}
                            edges={{ end: !tail.continuesRight }}
                            className={classnames(trackStyles.block, trackStyles.termTail, {
                                [trackStyles.cutRight]: tail.continuesRight,
                            })}
                            style={{
                                backgroundColor: project.paint.background,
                                borderColor: project.paint.borderColor,
                            }}
                            tip="Плановый срок проекта"
                            tipNote={
                                project.planEnd
                                    ? `до ${moment(project.planEnd, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")}; работ здесь нет`
                                    : ""
                            }
                            onCommit={commitTail}
                        />
                    )}

                    {planEnd !== null && (
                        <div
                            className={trackStyles.term}
                            style={{ left: planEnd - 2 }}
                            data-tip="Плановый срок проекта"
                            data-tip-note={
                                project.planEnd
                                    ? moment(project.planEnd, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")
                                    : ""
                            }
                        />
                    )}

                    {/* вехи видны и у свёрнутого проекта — они на строке самого проекта */}
                    {project.milestones.length > 0 && (
                        <HiddenMilestones
                            milestones={project.milestones}
                            paint={project.paint}
                            grid={grid}
                            columnsCount={columnsCount}
                            viewport={viewport}
                            onReveal={onReveal}
                        />
                    )}

                    {project.milestones.length > 0 && (
                        <MilestonesLayer
                            projectId={project.id}
                            milestones={project.milestones}
                            paint={project.paint}
                            summary={project.summary}
                            grid={grid}
                            columnsCount={columnsCount}
                            activeKey={activeMilestoneKey}
                            handlers={milestoneHandlers}
                            onMove={canEditProject ? moveMilestone : undefined}
                            linesHost={milestoneLines}
                        />
                    )}
                </PlanTrack>
            </div>

            {/* Раскрытие — тот же Collapse с внутренним блоком, что у отделов табеля-плана */}
            <Collapse isOpen={!collapsed} keepChildrenMounted={false}>
                <div className={styles.rows}>
                    {detail === "employees" &&
                        project.rows.map((row) => (
                            <ProjectPlanRowView
                                key={`${row.kind}_${row.id}`}
                                row={row}
                                paint={project.paint}
                                label={project.fullInventoryNumber}
                                grid={grid}
                                monthBreaks={monthBreaks}
                                selectedSlot={selectedSlot}
                                orderId={project.id}
                                links={links}
                                editable={row.kind === "employer" && canEditRow(row.id)}
                                onOperation={onOperation}
                                conflicts={
                                    row.kind === "employer"
                                        ? rowRanges(`e/${row.id}`, (cells) => cells.employerId === row.id)
                                        : undefined
                                }
                                highlighted={row.kind === "employer" && isHighlighted(row.id)}
                                absences={row.kind === "employer" && absences ? absences.get(row.id) : undefined}
                                busy={row.kind === "employer" ? rowBusy(row.id) : undefined}
                                onBusyClick={onBusyClick}
                                days={days}
                                onOpenLinks={onOpenWorkLinks}
                                linkConnectable={linkConnectable}
                            />
                        ))}

                    {detail === "sections" &&
                        project.sections.map((section, index) => {
                            const key = sectionKey(project.id, section.id);
                            const opened = !!collapser.opened.get(key);
                            const previous = index > 0 ? project.sections[index - 1] : null;
                            // стадия — подзаголовком над первым её разделом; «Без раздела» без стадии
                            const stageCaption =
                                !!section.id && !!section.stage && (!previous || previous.stage !== section.stage);

                            return (
                                <React.Fragment key={key}>
                                    {stageCaption && (
                                        <ProjectPlanStageCaption
                                            stage={section.stage}
                                            width={PROJECT_NAME_WIDTH + grid.totalWidth}
                                        />
                                    )}

                                    <ProjectPlanSectionRow
                                        section={section}
                                        orderId={project.id}
                                        paint={project.paint}
                                        grid={grid}
                                        monthBreaks={monthBreaks}
                                        selectedSlot={selectedSlot}
                                        links={links}
                                        collapsed={!opened}
                                        collapseKey={key}
                                        onToggle={collapser.toggle}
                                        editable={canEditProject}
                                        days={days}
                                        onOperation={onOperation}
                                        onShiftFrom={onShiftFrom}
                                        conflicts={rowRanges(
                                            `s/${section.id}`,
                                            (cells) => cells.contentGuid === section.id,
                                            true
                                        )}
                                        onOpenLinks={onOpenSectionLinks}
                                        linkConnectable={linkConnectable}
                                    />

                                    {opened &&
                                        section.rows.map((row) => (
                                            <ProjectPlanRowView
                                                key={`${key}_${row.kind}_${row.id}`}
                                                row={row}
                                                paint={project.paint}
                                                label={section.code || project.fullInventoryNumber}
                                                grid={grid}
                                                monthBreaks={monthBreaks}
                                                selectedSlot={selectedSlot}
                                                orderId={project.id}
                                                links={links}
                                                nested={true}
                                                editable={row.kind === "employer" && canEditRow(row.id)}
                                                onOperation={onOperation}
                                                conflicts={
                                                    row.kind === "employer"
                                                        ? rowRanges(
                                                              `s/${section.id}/${row.id}`,
                                                              (cells) =>
                                                                  cells.employerId === row.id &&
                                                                  cells.contentGuid === section.id
                                                          )
                                                        : undefined
                                                }
                                                sectionId={section.id}
                                                highlighted={
                                                    row.kind === "employer" && isHighlighted(row.id, section.id)
                                                }
                                                absences={
                                                    row.kind === "employer" && absences
                                                        ? absences.get(row.id)
                                                        : undefined
                                                }
                                                busy={row.kind === "employer" ? rowBusy(row.id, section.id) : undefined}
                                                onBusyClick={onBusyClick}
                                                days={days}
                                                onOpenLinks={onOpenWorkLinks}
                                                linkConnectable={linkConnectable}
                                            />
                                        ))}
                                </React.Fragment>
                            );
                        })}
                </div>
            </Collapse>
        </div>
    );
});

ProjectSection.displayName = "ProjectSection";
